import type {
  Table as ReactTable,
  ColumnDef,
  SortingState,
  PaginationState,
  RowSelectionState,
  Row,
} from "@tanstack/react-table";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { IStaticDropdownOption } from "../primitives";

// Core data types
export type IViewMode = "table" | "card";
export type IDataType = "string" | "number" | "date" | "boolean" | "dropdown" | "time";

// Filter types
export enum FilterOperator {
  Equal = "equal",
  NotEqual = "not_equal",
  Less = "less",
  LessOrEqual = "less_or_equal",
  Greater = "greater",
  GreaterOrEqual = "greater_or_equal",
  In = "in",
  NotIn = "not_in",
  Between = "between",
  NotBetween = "not_between",
  Contains = "contains",
  BeginsWith = "begins_with",
  EndsWith = "ends_with",
  IsNull = "is_null",
  IsNotNull = "is_not_null",
}

export enum LogicalOperator {
  AND = "AND",
  OR = "OR",
}

export interface IFilterColumn {
  field: string;
  headerName: string;
  filterable: boolean;
  dataType: IDataType;
  options?: IStaticDropdownOption[];
  renderInput?: (props: {
    value: any;
    onChange: (val: any) => void;
    operator: string;
  }) => ReactNode;
}

export interface IApiFilter {
  field: string;
  operator: FilterOperator;
  headerName: string;
  value: string | string[] | number | number[] | boolean;
}

export interface IFilterModel extends Omit<IApiFilter, "headerName"> {
  id?: string;
  headerName: string;
}

// Table component
export interface IDataTable<TData> {
  table: ReactTable<TData>;
  loading?: boolean;
  pageSizeOptions?: number[];
  checkbox?: boolean;
  // Optional module name for permission / tracking on consumers
  moduleName?: string;
  filterDrawerOpen?: boolean;
  setFilterDrawerOpen?: Dispatch<SetStateAction<boolean>>;
  filterColumns?: ColumnDef<TData>[];
  filterModel?: IFilterModel[];
  setFilterModel?: Dispatch<SetStateAction<IFilterModel[]>>;

  expandable?: boolean;
  renderExpandedRow?: (row: Row<TData>) => ReactNode;
  isPagination?: boolean;
  filterLabels?: IFilterLabels;
  isMulti?: boolean;
  noRowsLabel?: string;
  showLabels?: boolean;
  maxHeight?: string;
}

// Pagination
export interface ITableGridPagination<TData> {
  table: ReactTable<TData>;
  pageSizeOptions?: number[];
  showLabels?: boolean;
}

// Server table types
export interface IServerTableResponse<TData> {
  data: TData[];
  count: number;
  extra?: Record<string, string | number | boolean | null>;
}

export interface IServerFetcherParams {
  page: number;
  pageSize: number;
  sorting: SortingState;
  filters: IApiFilter[];
  search?: string;
}

export type ServerFetcher<TData> = (
  params: IServerFetcherParams,
) => Promise<IServerTableResponse<TData>>;

// Row constraint for server table
export interface IRowWithId {
  id: string | number;
}

// Server table hook
export interface IUseServerTable<TData extends IRowWithId> {
  columns: ColumnDef<TData>[];
  fetchRecords: ServerFetcher<TData>;
  initialPageSize?: number;
  mode?: IViewMode;
  enabled?: boolean;
}

// Server table state
export interface IServerTableState<TData> {
  data: TData[];
  total: number;
  loading: boolean;
  sorting: SortingState;
  pagination: PaginationState;
  filters: IFilterModel[];
  search: string;
  rowSelection: RowSelectionState;
}

// Filter drawer
export interface ITableFilterDrawer {
  open: boolean;
  onClose: () => void;
  columns: IFilterColumn[];
  filterModel: IFilterModel[];
  onChangeFilter: Dispatch<SetStateAction<IFilterModel[]>>;
  onClear: () => void;
  onApply: () => void;
  activeFilterCount: number;
  filterLabels?: IFilterLabels;
  isMulti?: boolean;
  timezone?: string;
  filterLogic?: string;
  onChangeFilterLogic?: (logic: string) => void;
}

export interface IFilterLabels {
  tableTitleLabel?: string;
  buttonLabel?: string;
  columnLabel?: string;
  operatorLabel?: string;
  valueLabel?: string;
  selectColumnLabel?: string;
  selectOperatorLabel?: string;
  selectValueLabel?: string;
  startLabel?: string;
  endLabel?: string;
  applyFilterLabel?: string;
  clearLabel?: string;
  trueLabel?: string;
  falseLabel?: string;
  columnErrorLabel?: string;
  operatorErrorLabel?: string;
  rangeErrorLabel?: string;
  valueErrorLabel?: string;
}

// Operator option for dropdowns
export interface IOperatorOption {
  label: string;
  value: FilterOperator;
}

// Column option for dropdowns
export interface IColumnOption {
  label: string;
  value: string;
  dataType: IDataType;
}

export interface IUseStaticTable<T, TValue = T[keyof T]> {
  data: T[];
  columns: ColumnDef<T, TValue>[];
  initialPageSize?: number;
  autoResetPageIndex?: boolean;
}

export enum SortOrderEnum {
  ASC = "asc",
  DESC = "desc",
}

export enum GridViewMode {
  CARD = "card",
  TABLE = "table",
}

// TableFilter component
export interface IDataTableFilter<TData> {
  table: ReactTable<TData>;
  filterDrawerOpen: boolean;
  setFilterDrawerOpen: Dispatch<SetStateAction<boolean>>;
  filterColumns: ColumnDef<TData>[];
  filterModel: IFilterModel[];
  setFilterModel: Dispatch<SetStateAction<IFilterModel[]>>;
  filterLabels?: IFilterLabels;
  isMulti?: boolean;
  timezone?: string;
}